import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Sparkles, 
  Users, 
  TrendingUp, 
  Shield, 
  Zap,
  Target,
  Heart,
  ArrowRight,
  Star,
  Award,
  Globe
} from "lucide-react";

const Values = () => {
  const values = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Innovation & Creativity",
      desc: "We constantly explore new ideas and technologies to deliver modern, future-ready digital solutions.",
      gradient: "from-purple-500 to-pink-500",
      stats: "500+ Innovative Projects"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Client-First Approach",
      desc: "Every decision starts with understanding our clients' needs and delivering measurable value.",
      gradient: "from-blue-500 to-cyan-500",
      stats: "99% Client Satisfaction"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Long-Term Growth",
      desc: "We focus on sustainable systems that scale with your business, not short-term fixes.",
      gradient: "from-green-500 to-emerald-500",
      stats: "250+ Growing Partners"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Transparency & Trust",
      desc: "Clear communication, honest timelines, and ethical practices form the foundation of our work.",
      gradient: "from-indigo-500 to-blue-500",
      stats: "100% Transparent Process"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Quality & Performance",
      desc: "We build fast, secure, and reliable products that meet modern performance standards.",
      gradient: "from-amber-500 to-orange-500",
      stats: "Enterprise-Grade Quality"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Impact",
      desc: "Creating solutions that make education accessible and effective across 15+ countries.",
      gradient: "from-teal-500 to-cyan-500",
      stats: "15+ Countries Served"
    }
  ];

  const testimonials = [
    {
      quote: "Their client-first approach transformed our digital campus.",
      author: "University Director",
      role: "Higher Education"
    },
    {
      quote: "Innovative solutions that actually scale with our startup growth.",
      author: "Tech Startup CEO",
      role: "EdTech Industry"
    },
    {
      quote: "Transparency and quality that sets a new industry standard.",
      author: "School District Manager",
      role: "K-12 Education"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Values | Skyreti EduCore</title>
        <meta
          name="description"
          content="Discover the core values that drive Skyreti EduCore — innovation, trust, quality, and long-term growth."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-24 pb-20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-cyan-50/50" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-blue-100 rounded-full">
                <Target className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">What Drives Us</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Core Values</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                These principles guide every project, partnership, and decision we make at Skyreti EduCore.
              </p>
              
              {/* Mini Stats */}
              <div className="flex flex-wrap justify-center gap-6 mb-12">
                {["15+ Countries", "500+ Projects", "99% Satisfaction", "250+ Clients"].map((stat, index) => (
                  <motion.div
                    key={stat}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm"
                  >
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span className="font-semibold text-gray-900">{stat}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Values Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    y: -8,
                    transition: { duration: 0.2 }
                  }}
                  className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                >
                  {/* Gradient Corner */}
                  <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden rounded-tr-2xl">
                    <div className={`absolute -right-12 -top-12 w-32 h-32 bg-gradient-to-br ${value.gradient} opacity-10 group-hover:opacity-20 transition-opacity`} />
                  </div>

                  {/* Icon */}
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${value.gradient} bg-opacity-10 mb-6`}>
                    <div className={`text-transparent bg-clip-text bg-gradient-to-r ${value.gradient}`}>
                      {value.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{value.desc}</p>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    <span className="text-sm font-semibold bg-gradient-to-r bg-clip-text text-transparent from-gray-600 to-gray-400">
                      {value.stats}
                    </span>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Values in Action
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                See how our values translate into real-world impact
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white rounded-2xl p-6 shadow-lg"
                >
                  <div className="mb-4">
                    <Heart className="w-8 h-8 text-red-100 fill-red-500" />
                  </div>
                  <p className="text-gray-900 text-lg font-medium mb-6">
                    "{testimonial.quote}"
                  </p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section - Connected to JoinUs */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-3xl"
            >
              {/* Background */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500" />
              <div
  className="absolute inset-0 opacity-40 pointer-events-none"
  style={{
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  }}
/>

              {/* Content */}
              <div className="relative px-8 py-12 md:py-16 text-center">
                <Award className="w-16 h-16 text-white/30 mx-auto mb-6" />
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Values That Drive Real Impact
                </h2>
                <p className="text-xl text-blue-50 max-w-2xl mx-auto mb-10">
                  Our values aren't just words — they shape how we design, develop, and support digital solutions for schools, startups, and organizations worldwide.
                </p>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {/* Connect to JoinUs page */}
                    <Link
                      to="/joinus"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      <Users className="w-5 h-5" />
                      Work With Us
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </motion.div>
                  
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to="/services"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-all duration-300"
                    >
                      Explore Services
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </motion.div>
                </div>

                {/* Trust Badge */}
                <div className="mt-12 flex items-center justify-center gap-3 text-sm text-white/80">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span>Trusted by 250+ educational institutions and startups</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Commitment Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Our Commitment to You
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                We measure our success by your growth, satisfaction, and the positive impact we create together in the education and startup ecosystems.
              </p>
              <div className="inline-flex items-center gap-2 text-blue-600 font-semibold">
                <span>Ready to make an impact?</span>
                <Link to="/join" className="hover:underline">
                  Let's connect →
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Values;